import axios, { AxiosInstance } from 'axios';

export interface ApiResponse<T = any> {
  data: T;
  status: boolean;
  statusCode: number;
  errorMessage: string;
}

export class BaseService<T = any> {
  protected baseURL: string;
  protected axiosInstance: AxiosInstance;

  constructor(path: string) {
    this.baseURL = `/api/${path}`;
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001'
    });
  }

  protected async get(endpoint: string) {
    return await this.axiosInstance.get(`${this.baseURL}${endpoint}`);
  }

  protected async post(endpoint: string, data: any) {
    return await this.axiosInstance.post(`${this.baseURL}${endpoint}`, data);
  }

  protected async put(endpoint: string, data: any) {
    return await this.axiosInstance.put(`${this.baseURL}${endpoint}`, data);
  }

  protected async delete(endpoint: string) {
    return await this.axiosInstance.delete(`${this.baseURL}${endpoint}`);
  }

  async getAll(
    formData?: Record<string, any>,
    url: string = this.baseURL
  ): Promise<ApiResponse<T>> {
    try {
      const apiResponse = await this.axiosInstance.get(url, { params: formData });
      return {
        data: apiResponse.data,
        status: true,
        statusCode: apiResponse.status,
        errorMessage: "",
      };
    } catch (error) {
      throw error;
    }
  }

  async getById(
    id: string | number,
    formData?: Record<string, any>,
    url: string = `${this.baseURL}/${id}`
  ): Promise<ApiResponse<T>> {
    try {
      const apiResponse = await this.axiosInstance.get(url, { params: formData });
      return {
        data: apiResponse.data,
        status: true,
        statusCode: apiResponse.status,
        errorMessage: "",
      };
    } catch (error) {
      throw error;
    }
  }

  async add(
    data: T,
    formData?: Record<string, any>,
    url: string = this.baseURL
  ): Promise<{ data: T; statusCode: number }> {
    try {
      const res = await this.axiosInstance.post(url, data, { params: formData });
      return {
        data: res.data,
        statusCode: res.status,
      };
    } catch (error) {
      throw error;
    }
  }

  async update(data: T, url: string = this.baseURL): Promise<{ data: T; statusCode: number }> {
    try {
      const res = await this.axiosInstance.put(url, data);
      return {
        data: res.data,
        statusCode: res.status,
      };
    } catch (error) {
      throw error;
    }
  }

  async remove(id: string | number, url: string = `${this.baseURL}`): Promise<void> {
    try {
      await this.axiosInstance.delete(url, { params: { id } });
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
}
